import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Autocomplete, Box, Button, Grid, MenuItem, Stack, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import ItemSupplierAddModal from '@/modules/inventory/items/components/modals/ItemSupplierAddModal';
import { createItemSupplierOption } from '@/modules/inventory/items/models/ItemModel';
import { getItemUomBackendOptions } from '@/modules/inventory/items/redux/ItemUomCRUD';
import useRequestMaterialItems from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialItems';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';

import { InventoryReceiptFormData, ReceiptLine } from '../types';
import ReceiptLinesTable from './ReceiptLinesTable';

interface ReceiptFormProps {
  formData: InventoryReceiptFormData;
  receiptId?: string;
  statusOptions: Array<{ id?: string; name: string; code?: string; color?: string; is_default?: boolean }>;
  purchaseOptions: Array<{ id: string; name: string }>;
  returnSourceOptions: Array<{ id: string; name: string }>;
  supplierOptions: Array<{ id: string; name: string; code?: string }>;
  purchaseLoading?: boolean;
  returnSourceLoading?: boolean;
  supplierLoading?: boolean;
  saving?: boolean;
  posting?: boolean;
  canEdit?: boolean;
  canPost?: boolean;
  onPurchaseSearch: (search: string) => void;
  onReturnSourceSearch: (search: string) => void;
  onSupplierSearch: (search: string) => void;
  onChangeForm: (updates: Partial<InventoryReceiptFormData>) => void;
  onChangeLine: (lineId: string, updates: Partial<ReceiptLine>) => void;
  onSave: () => void;
  onPost: () => void;
}

interface WarehouseAutocompleteProps {
  companyId: string;
  value?: string;
  warehouseName?: string;
  disabled?: boolean;
  onChange: (warehouseId: string | null, warehouseName?: string | null) => void;
}

interface AddOptionCustomDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
}

const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

const getStatusValue = (status: InventoryReceiptFormData['status']) => {
  if (typeof status === 'object' && status !== null) {
    return String(status.id || status.code || '').trim();
  }

  return String(status || '').trim();
};

const resolveStatusOption = (
  statusOptions: ReceiptFormProps['statusOptions'],
  formData: InventoryReceiptFormData,
) => {
  const statusValue = getStatusValue(formData.status);
  const statusCode = String(
    (typeof formData.status === 'object' && formData.status !== null ? formData.status.code : '') ||
    formData.status_code ||
    '',
  ).trim();
  const statusName = String(
    (typeof formData.status === 'object' && formData.status !== null ? formData.status.name : '') ||
    formData.status_display ||
    '',
  ).trim();

  return statusOptions.find((option) => (
    option.id === statusValue ||
    option.code === statusValue ||
    option.id === statusCode ||
    option.code === statusCode ||
    option.name === statusName
  )) || null;
};

function WarehouseAutocomplete({
  companyId,
  value,
  warehouseName,
  disabled = false,
  onChange,
}: WarehouseAutocompleteProps) {
  const { warehouseOptions, loading } = useRequestMaterialWarehouses({
    companyId: companyId || '',
    skip: !companyId,
    onError: () => {},
  });

  const effectiveOptions = (warehouseOptions && warehouseOptions.length > 0)
    ? warehouseOptions
    : (value ? [{ id: value, name: warehouseName || value }] : []);

  return (
    <AddButtonAutocomplete
      options={effectiveOptions}
      value={effectiveOptions.find((option) => option.id === value) || (value ? { id: value, name: warehouseName || value } : null)}
      onChange={(_event, selectedValue) => {
        const selected = selectedValue as { id: string; name: string } | null;
        onChange(selected?.id || null, selected?.name || null);
      }}
      placeholder='Chọn kho nhập'
      loading={loading}
      noOptionsText='Không tìm thấy kho'
      disabled={disabled}
      fullWidth
    />
  );
}

export default function ReceiptForm({
  formData,
  receiptId,
  statusOptions,
  purchaseOptions,
  returnSourceOptions,
  supplierOptions,
  purchaseLoading = false,
  returnSourceLoading = false,
  supplierLoading = false,
  saving = false,
  posting = false,
  canEdit = true,
  canPost = true,
  onPurchaseSearch,
  onReturnSourceSearch,
  onSupplierSearch,
  onChangeForm,
  onChangeLine,
  onSave,
  onPost,
}: ReceiptFormProps) {
  const [manualSupplierOptions, setManualSupplierOptions] = React.useState<Array<{ id: string; name: string; code?: string }>>([]);
  const effectiveSupplierOptions = React.useMemo(() => {
    const merged = [...supplierOptions, ...manualSupplierOptions];
    const unique = new Map<string, { id: string; name: string; code?: string }>();
    merged.forEach((supplier) => {
      if (!unique.has(supplier.id)) {
        unique.set(supplier.id, supplier);
      }
    });
    return Array.from(unique.values());
  }, [supplierOptions, manualSupplierOptions]);

  const selectedPurchase = purchaseOptions.find((option) => option.id === formData.purchase_id) || null;
  const selectedSupplier = effectiveSupplierOptions.find((option) => option.id === formData.supplier_id) || null;
  const isFromPurchase = formData.receipt_type === 'from_purchase';
  const isReturn = formData.receipt_type === 'return';
  const selectedReturnSource = returnSourceOptions.find((option) => option.id === formData.return_source_issue_id) || null;
  const currentStatus = React.useMemo(
    () => resolveStatusOption(statusOptions, formData),
    [formData.status, formData.status_code, formData.status_display, statusOptions],
  );
  const statusSelectValue = currentStatus?.id || getStatusValue(formData.status);
  const effectiveStatusOptions = React.useMemo(() => {
    const normalizedOptions = statusOptions.filter((status) => Boolean(status.id));

    if (!statusSelectValue || normalizedOptions.some((status) => status.id === statusSelectValue)) {
      return normalizedOptions;
    }

    return [
      ...normalizedOptions,
      {
        id: statusSelectValue,
        name: formData.status_display || (typeof formData.status === 'object' ? formData.status.name : '') || statusSelectValue,
        code: formData.status_code || (typeof formData.status === 'object' ? formData.status.code : '') || '',
        color: formData.status_color || (typeof formData.status === 'object' ? formData.status.color : ''),
      },
    ];
  }, [formData.status, formData.status_code, formData.status_color, formData.status_display, statusOptions, statusSelectValue]);
  const isPosted = ((currentStatus?.code || formData.status_code || getStatusValue(formData.status)) || '').toUpperCase() === 'POSTED';
  const isReadOnly = isPosted || !canEdit;
  const receivedDate = formData.received_at ? new Date(formData.received_at) : null;
  const defaultStatusId = React.useMemo(
    () => effectiveStatusOptions.find((status) => status.is_default)?.id || effectiveStatusOptions[0]?.id || '',
    [effectiveStatusOptions],
  );
  const [uomOptions, setUomOptions] = React.useState<Array<{ id: string; name: string; code: string }>>([]);
  const { itemOptions, loading: itemLoading } = useRequestMaterialItems({
    companyId: formData.company_id,
    skip: !formData.company_id,
    onError: () => {},
  });

  React.useEffect(() => {
    let mounted = true;
    getItemUomBackendOptions()
      .then((options) => {
        if (!mounted) return;
        setUomOptions(options.map((option) => ({ id: option.id, name: option.name, code: option.name })));
      })
      .catch(() => {
        if (!mounted) return;
        setUomOptions([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleDeleteLines = (lineIds: string[]) => {
    if (!lineIds.length) {
      return;
    }
    const remainingLines = (formData.lines || []).filter((line) => !lineIds.includes(line.id));
    onChangeForm({ lines: remainingLines });
  };

  const handleAddLine = () => {
    const currentLines = formData.lines || [];
    const nextSortOrder = currentLines.length > 0
      ? Math.max(...currentLines.map((line) => Number(line.sort_order) || 0)) + 1
      : 1;

    const newLine: ReceiptLine = {
      id: `manual-${Date.now()}`,
      sort_order: nextSortOrder,
      item_id: '',
      item_code: '',
      item_name: '',
      uom_id: '',
      uom_name: '',
      received_qty: '0',
      accepted_qty: '0',
      rejected_qty: '0',
      unit_cost: '0',
      line_amount: '0',
      purchase_line_id: null,
      lot_no: '',
      serial_numbers: '',
      note: '',
    };

    onChangeForm({ lines: [...currentLines, newLine] });
  };

  const handleReceiptTypeChange = (nextType: string) => {
    onChangeForm({
      receipt_type: nextType,
      lines: [],
      purchase_id: null,
      purchase_no: null,
      return_source_issue_id: null,
      return_source_issue_no: null,
      supplier_id: '',
      supplier_name: '',
      status: defaultStatusId || '',
    });
  };

  return (
    <Stack spacing={4} sx={{ pt: 1, pb: 3 }}>

      <Grid container rowSpacing={0} columnSpacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label='Mã phiếu nhập' labelWidth={130}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', fontFamily: '"Roboto Mono", monospace' }}>
              {formData.receipt_no || 'RC####-##'}
            </Typography>
          </LabeledField>
           <LabeledField label='Loại nhập' labelWidth={130}>
            <TextField
              select
              size='small'
              value={formData.receipt_type}
              onChange={(event) => handleReceiptTypeChange(event.target.value)}
              disabled={isReadOnly}
              fullWidth
            >
              <MenuItem value='from_purchase'>Nhập từ phiếu mua</MenuItem>
              <MenuItem value='direct'>Nhập trực tiếp</MenuItem>
              <MenuItem value='return'>Nhập trả về</MenuItem>
            </TextField>
          </LabeledField>
          {isFromPurchase && (
            <LabeledField label='PO nguồn' labelWidth={130}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', minWidth: 0 }}>
                <AddButtonAutocomplete
                  options={purchaseOptions}
                  value={selectedPurchase}
                  onChange={(_event, value) => {
                    const selected = value as { id: string; name: string } | null;
                    onChangeForm({ purchase_id: selected?.id || null, purchase_no: selected?.name || null });
                  }}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      onPurchaseSearch(value);
                    }
                  }}
                  placeholder='Chọn phiếu mua hàng để tự sinh dòng nhập'
                  loading={purchaseLoading}
                  noOptionsText='Không tìm thấy phiếu mua hàng'
                  disabled={isReadOnly}
                  fullWidth
                  sx={{ width: '100%', minWidth: 0 }}
                />
              </Box>
            </LabeledField>
          )}

          {isReturn && (
            <LabeledField label='Phiếu xuất nguồn' labelWidth={130}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%', minWidth: 0 }}>
                <AddButtonAutocomplete
                  options={returnSourceOptions}
                  value={selectedReturnSource}
                  onChange={(_event, value) => {
                    const selected = value as { id: string; name: string } | null;
                    onChangeForm({
                      return_source_issue_id: selected?.id || null,
                      return_source_issue_no: selected?.name || null,
                    });
                  }}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      onReturnSourceSearch(value);
                    }
                  }}
                  placeholder='Chọn phiếu xuất nguyên vật liệu để lấy dòng trả về'
                  loading={returnSourceLoading}
                  noOptionsText='Không tìm thấy phiếu xuất'
                  disabled={isReadOnly}
                  fullWidth
                  sx={{ width: '100%', minWidth: 0 }}
                />
              </Box>
            </LabeledField>
          )}
         
          <LabeledField label='Nhà cung cấp' labelWidth={130}>
            <AddButtonAutocomplete
              options={effectiveSupplierOptions}
              value={selectedSupplier || (formData.supplier_id ? { id: formData.supplier_id, name: formData.supplier_name || '' } : null)}
              onChange={(_event, value) => {
                const selected = value as { id: string; name: string } | null;
                onChangeForm({
                  supplier_id: selected?.id || '',
                  supplier_name: selected?.name || '',
                });
              }}
              CustomDialog={SupplierAddModal}
              canAddNew={!isReadOnly && !isFromPurchase}
              onAddNewCustom={async (data) => {
                if (isReadOnly || isFromPurchase) {
                  return selectedSupplier || { id: '', name: '', code: '' };
                }
                const created = await createItemSupplierOption(data.name, formData.company_id, {
                  email: data.email,
                  phone: data.phone,
                  address: data.address,
                });

                const createdOption = {
                  id: created.id,
                  name: created.name,
                  code: '',
                };

                setManualSupplierOptions((current) => [...current, createdOption]);
                onChangeForm({
                  supplier_id: created.id,
                  supplier_name: created.name,
                });

                return createdOption;
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  onSupplierSearch(value);
                }
              }}
              placeholder={isFromPurchase ? 'Nhà cung cấp lấy theo PO' : 'Chọn nhà cung cấp'}
              loading={supplierLoading}
              noOptionsText='Không tìm thấy nhà cung cấp'
              disabled={isFromPurchase || isReadOnly}
              filterOptions={(options) => options}
              fullWidth
            />
          </LabeledField>
          
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabeledField label='Kho nhập' labelWidth={130}>
            <WarehouseAutocomplete
              companyId={formData.company_id}
              value={formData.warehouse_id || ''}
              warehouseName={formData.warehouse_name || ''}
              disabled={isReadOnly}
              onChange={(warehouseId, warehouseName) => onChangeForm({
                warehouse_id: warehouseId || '',
                warehouse_name: warehouseName || '',
              })}
            />
          </LabeledField>
          <LabeledField label='Trạng thái' labelWidth={130}>
            <Autocomplete
              options={effectiveStatusOptions}
              value={currentStatus}
              onChange={(_event, newValue) => {
                const selectedStatus = newValue as { id?: string; name: string; code?: string; color?: string } | null;
                onChangeForm({
                  status: selectedStatus?.id || '',
                  status_code: selectedStatus?.code || '',
                  status_display: selectedStatus?.name || '',
                  status_color: selectedStatus?.color || '',
                });
              }}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              disabled={isReadOnly}
              renderOption={(props, option) => (
                <li {...props} key={option.id || option.name} style={{ padding: 0, margin: 0 }}>
                  <Typography sx={{ color: option.color || 'inherit', paddingLeft: '24px', py: 0.5 }}>
                    {option.name}
                  </Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  placeholder='Chọn trạng thái'
                  size='small'
                  variant='standard'
                  InputProps={{
                    ...params.InputProps,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: currentStatus?.color || formData.status_color || 'inherit',
                      fontWeight: currentStatus?.color || formData.status_color ? 500 : 'inherit',
                    },
                  }}
                />
              )}
              noOptionsText='Không tìm thấy trạng thái'
              fullWidth
            />
          </LabeledField>
          <LabeledField label='Thời điểm nhận' labelWidth={130}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={receivedDate && !isNaN(receivedDate.getTime()) ? receivedDate : null}
                onChange={(value) => onChangeForm({ received_at: value ? value.toISOString() : '' })}
                disabled={isReadOnly}
                format='dd/MM/yyyy'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </LabeledField>
        </Grid>
      </Grid>

      

      

      <ReceiptLinesTable
        lines={formData.lines || []}
        onChangeLine={onChangeLine}
        onDeleteLines={handleDeleteLines}
        itemOptions={itemOptions}
        uomOptions={uomOptions}
        itemLoading={itemLoading}
        allowAddItem={formData.receipt_type !== 'from_purchase'}
        onAddItem={handleAddLine}
        disabled={isReadOnly}
      />
      <Box sx={{ mt: 2, width: '100%' }}>
        <LabeledField label='Ghi chú' labelWidth={70} alignItems='start'>
          <TextareaAutosize
            minRows={2}
            value={formData.note}
            onChange={(event) => onChangeForm({ note: event.target.value })}
            disabled={isReadOnly}
            style={sharedFormStyles.additionalTextarea(!isReadOnly)}
          />
        </LabeledField>
         </Box>

      <Box sx={{ pt: 2, width: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='flex-end' spacing={2}>
          <Button variant='contained' onClick={onSave} disabled={isReadOnly || saving || posting} size='small'>
            {saving ? 'Đang lưu...' : 'Lưu phiếu nhập'}
          </Button>
          <Button variant='outlined' color='success' onClick={onPost} disabled={isPosted || saving || posting || !canPost} size='small'>
            {posting ? 'Đang ghi sổ...' : isPosted ? 'Đã ghi sổ' : 'Post (Ghi sổ)'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
